import type { EventSourceMessage } from '@microsoft/fetch-event-source';
import { fetchEventSource } from '@microsoft/fetch-event-source';

const BASE_URL = '/backend-api';

type SseCloseReason = 'closed' | 'error' | 'timeout' | 'http_error' | 'user_abort';

export interface SseCloseMeta {
  connectionId: string;
  attempt: number;
  maxReconnects: number;
  reconnectCount: number;
  retriesLeft: number;
  durationMs: number;
  reason: SseCloseReason;
}

class FatalSseError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'FatalSseError';
  }
}

class RetryableSseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RetryableSseError';
  }
}

function asError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
}

function isUserAbortSignal(
  data: { controller: AbortController; isUserAbort?: () => boolean },
  abortedByUser: boolean,
) {
  return abortedByUser || data.controller.signal.aborted || Boolean(data.isUserAbort?.());
}

function isHeartbeatMessage(ev: EventSourceMessage): boolean {
  if (!ev.data) {
    return false;
  }

  try {
    const payload = JSON.parse(ev.data) as { type?: unknown };
    return payload?.type === 'ping';
  }
  catch {
    return false;
  }
}

export async function connect(data: {
  url: string;
  body?: Record<string, unknown>;
  onopen: (response: Response) => Promise<void>;
  onmessage: (ev: EventSourceMessage) => void;
  onclose: (meta?: SseCloseMeta) => void;
  onerror: (ev: Event, meta?: SseCloseMeta) => void;
  controller: AbortController;
  method: string;
  headers?: Record<string, string>;
  firstTokenTimeoutMs?: number;
  maxReconnects?: number;
  isUserAbort?: () => boolean;
}) {
  const firstTokenTimeoutMs = data.firstTokenTimeoutMs ?? 45000;
  const maxReconnects = data.maxReconnects ?? 1;
  const requestBody = data.body ? JSON.stringify(data.body) : undefined;
  let reconnectCount = 0;
  let attempt = 0;

  while (true) {
    attempt += 1;
    const connectionId = crypto.randomUUID();
    const startedAt = Date.now();
    let receivedFirstMessage = false;
    let timedOutBeforeFirstToken = false;
    let abortedByUser = false;
    const requestController = new AbortController();

    const abortByUser = () => {
      abortedByUser = true;
      requestController.abort();
    };
    data.controller.signal.addEventListener('abort', abortByUser, { once: true });

    const firstTokenTimer = setTimeout(() => {
      if (!receivedFirstMessage) {
        timedOutBeforeFirstToken = true;
        requestController.abort();
      }
    }, firstTokenTimeoutMs);

    const buildMeta = (reason: SseCloseReason): SseCloseMeta => ({
      connectionId,
      attempt,
      maxReconnects,
      reconnectCount,
      retriesLeft: Math.max(0, maxReconnects - reconnectCount),
      durationMs: Date.now() - startedAt,
      reason,
    });

    console.log(`[SSE-${connectionId}] status: connecting`);
    console.log(`[SSE-${connectionId}] attempt=${attempt} reconnects=${reconnectCount}/${maxReconnects}`);
    console.log(`[SSE-${connectionId}] ConversationId:`, data.body?.conversationId || 'new');

    try {
      await fetchEventSource(`${BASE_URL}${data.url}`, {
        method: data.method,
        headers: data.headers,
        body: requestBody,
        openWhenHidden: true,
        signal: requestController.signal,
        onopen: async (response) => {
          if (response.status >= 400) {
            throw new FatalSseError(
              `SSE request failed with status ${response.status}`,
              response.status,
            );
          }
          console.log(`[SSE-${connectionId}] status: open (${Date.now() - startedAt}ms)`);
          await data.onopen(response);
        },
        onmessage: (ev) => {
          if (!receivedFirstMessage && !isHeartbeatMessage(ev)) {
            receivedFirstMessage = true;
            clearTimeout(firstTokenTimer);
          }
          data.onmessage(ev);
        },
        onclose: () => {
          // handle final close status after fetchEventSource resolves
        },
        onerror: (error) => {
          if (error instanceof Error) {
            throw error;
          }
          throw new RetryableSseError(`SSE stream error: ${String(error)}`);
        },
      });

      const userAbort = isUserAbortSignal(data, abortedByUser);
      const shouldRetry = !userAbort && !receivedFirstMessage && reconnectCount < maxReconnects;
      if (shouldRetry) {
        reconnectCount += 1;
        const meta = buildMeta(timedOutBeforeFirstToken ? 'timeout' : 'closed');
        console.warn(
          `[SSE-${connectionId}] status: retrying (${reconnectCount}/${maxReconnects}) after ${meta.durationMs}ms`,
        );
        continue;
      }

      const reason: SseCloseReason = userAbort
        ? 'user_abort'
        : timedOutBeforeFirstToken
          ? 'timeout'
          : 'closed';

      const closeMeta = buildMeta(reason);
      console.log(`[SSE-${connectionId}] status: closed (total: ${closeMeta.durationMs}ms)`);
      data.onclose(closeMeta);
      return;
    }
    catch (caughtError) {
      const userAbort = isUserAbortSignal(data, abortedByUser);
      const isFatal = caughtError instanceof FatalSseError;
      const reason: SseCloseReason = userAbort
        ? 'user_abort'
        : isFatal
          ? 'http_error'
          : timedOutBeforeFirstToken
            ? 'timeout'
            : 'error';

      const canRetry = !userAbort && !isFatal && reconnectCount < maxReconnects;
      if (canRetry) {
        reconnectCount += 1;
        const meta = buildMeta(reason);
        console.warn(
          `[SSE-${connectionId}] status: retrying (${reconnectCount}/${maxReconnects}) after ${meta.durationMs}ms`,
        );
        continue;
      }

      const finalMeta = buildMeta(reason);
      if (userAbort) {
        console.log(`[SSE-${connectionId}] status: closed (user abort, ${finalMeta.durationMs}ms)`);
        data.onclose(finalMeta);
        return;
      }

      console.error(`[SSE-${connectionId}] status: failed after ${finalMeta.durationMs}ms`, caughtError);
      data.onerror(new Event('error'), finalMeta);
      throw asError(caughtError);
    }
    finally {
      clearTimeout(firstTokenTimer);
      data.controller.signal.removeEventListener('abort', abortByUser);
    }
  }
}
